import React from 'react';
import { ChevronRight, Globe, Server, HardDrive, KeyRound, Network, Eye, FolderOpen } from 'lucide-react';
import { cn } from '../../../lib/utils';
import { useDiagramInfo } from '../../../hooks/useDiagramInfo';
import { useTranslation } from '../../../hooks/useTranslation';

/**
 * Breadcrumbs for materialized infrastructure view diagrams. Shows:
 *   Folder → View Type → Current View
 *
 * Only renders when the diagram has an `infra-view` ViewDefinition.
 * Clicking the folder link returns to the folder dashboard.
 */
interface InfraViewBreadcrumbsProps {
  diagramId: string;
  className?: string;
}

const VIEW_ICONS: Record<string, { icon: React.ElementType; labelKey: string }> = {
  'network-vpc': { icon: Network, labelKey: 'infraBreadcrumbs.views.vpc' },
  'public-exposure': { icon: Globe, labelKey: 'infraBreadcrumbs.views.publicExposure' },
  'compute-fleet': { icon: Server, labelKey: 'infraBreadcrumbs.views.computeFleet' },
  'data-stores': { icon: HardDrive, labelKey: 'infraBreadcrumbs.views.dataStores' },
  'iam-surface': { icon: KeyRound, labelKey: 'infraBreadcrumbs.views.iamSurface' },
};

export const InfraViewBreadcrumbs: React.FC<InfraViewBreadcrumbsProps> = ({
  diagramId,
  className,
}) => {
  const { t } = useTranslation('network');
  const { data: diagram } = useDiagramInfo(diagramId);

  if (!diagram || diagram.viewDefinition?.type !== 'infra-view') return null;

  const queryId = diagram.viewDefinition.viewQueryId ?? '';
  const queryPrefix = queryId.includes(':') ? queryId.split(':')[0] : queryId;
  const viewMeta = VIEW_ICONS[queryPrefix] ?? { icon: Eye, labelKey: 'infraBreadcrumbs.views.default' };
  const Icon = viewMeta.icon;

  return (
    <nav
      className={cn('flex items-center gap-1 text-sm text-muted-foreground', className)}
      aria-label={t('infraBreadcrumbs.ariaLabel')}
    >
      <a
        href={`/f/${diagram.folderId}`}
        className="inline-flex items-center gap-1 px-2 py-1 rounded-md hover:bg-muted transition-colors"
      >
        <FolderOpen className="size-3.5" />
        <span>{t('infraBreadcrumbs.folder')}</span>
      </a>
      <ChevronRight className="size-3 flex-shrink-0" />
      <span className="inline-flex items-center gap-1 px-2 py-1">
        <Icon className="size-3.5" />
        <span>{t(viewMeta.labelKey as string)}</span>
      </span>
      <ChevronRight className="size-3 flex-shrink-0" />
      <span className="px-2 py-1 font-medium text-foreground truncate max-w-[240px]">
        {diagram.name}
      </span>
      {diagram.viewDefinition.resourceCount !== undefined && (
        <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-muted ml-1">
          {t('infraBreadcrumbs.resources', { count: diagram.viewDefinition.resourceCount })}
        </span>
      )}
    </nav>
  );
};
